import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Wand2, Languages, Loader2, Check, X } from "lucide-react";
import { langFlag, langLabel } from "@/lib/langLabel";

// Admin-only helpers next to a message input: spell-correct the French draft,
// then translate it into the client's language with a preview to validate.
export const AiComposeTools = ({ text, onApply, clientLang, disabled }) => {
  const [busy, setBusy] = useState(null); // "correct" | "translate"
  const [preview, setPreview] = useState(null); // { corrected, translated }
  const hasText = (text || "").trim().length > 0;
  const canTranslate = hasText && clientLang && clientLang.toLowerCase() !== "fr";

  const correct = async () => {
    if (!hasText || busy) return;
    setBusy("correct");
    try {
      const { data } = await api.post("/admin/translate", { text, mode: "correct" });
      if (data.corrected) { onApply(data.corrected); toast.success("Orthographe corrigée"); }
    } catch { toast.error("Correction indisponible"); }
    finally { setBusy(null); }
  };

  const translate = async () => {
    if (!hasText || busy) return;
    setBusy("translate");
    try {
      const { data } = await api.post("/admin/translate", { text, target_lang: clientLang, mode: "translate" });
      setPreview({ corrected: data.corrected || text, translated: data.translated || "" });
    } catch { toast.error("Traduction indisponible"); }
    finally { setBusy(null); }
  };

  const useTranslation = () => { if (preview?.translated) onApply(preview.translated); setPreview(null); };

  return (
    <div className="relative flex items-center gap-1.5">
      <button type="button" data-testid="ai-correct-btn" onClick={correct} disabled={disabled || !hasText || !!busy}
        title="Corriger l'orthographe (français)"
        className="h-[42px] w-[42px] shrink-0 grid place-items-center border border-white/10 text-zinc-300 rounded-sm hover:border-aurora-cyan hover:text-aurora-cyan disabled:opacity-40 transition-colors">
        {busy === "correct" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
      </button>
      {canTranslate && (
        <button type="button" data-testid="ai-translate-btn" onClick={translate} disabled={disabled || !!busy}
          title={`Traduire en ${langLabel(clientLang)}`}
          className="h-[42px] px-2.5 shrink-0 grid place-items-center border border-white/10 text-zinc-300 rounded-sm hover:border-aurora-cyan hover:text-aurora-cyan disabled:opacity-40 transition-colors">
          {busy === "translate" ? <Loader2 className="w-4 h-4 animate-spin" /> : (
            <span className="flex items-center gap-1"><Languages className="w-4 h-4" /><span className="text-sm leading-none">{langFlag(clientLang)}</span></span>
          )}
        </button>
      )}

      {preview && (
        <div data-testid="ai-translate-preview" className="absolute bottom-[52px] right-0 z-30 w-[340px] max-w-[85vw] glass border border-aurora-cyan/30 rounded-md p-3 shadow-2xl">
          <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Français corrigé</div>
          <p className="text-xs text-zinc-300 mb-3 whitespace-pre-wrap break-words">{preview.corrected}</p>
          <div className="font-mono text-[9px] uppercase tracking-widest text-aurora-cyan mb-1 flex items-center gap-1">
            <span className="text-sm leading-none">{langFlag(clientLang)}</span> {langLabel(clientLang)}
          </div>
          <p className="text-sm text-white mb-3 whitespace-pre-wrap break-words" data-testid="ai-translated-text">{preview.translated}</p>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="ai-use-translation-btn" onClick={useTranslation} disabled={!preview.translated}
              className="flex-1 h-9 bg-aurora-cyan text-white font-display font-bold uppercase tracking-wider text-xs rounded-sm flex items-center justify-center gap-1.5 disabled:opacity-50">
              <Check className="w-3.5 h-3.5" /> Utiliser
            </button>
            <button type="button" data-testid="ai-cancel-translation-btn" onClick={() => setPreview(null)}
              className="h-9 px-3 border border-white/10 text-zinc-300 rounded-sm flex items-center justify-center hover:text-white transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
